import React, { useCallback, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { GOLD, NAVY, BORDER, ERROR } from "../../../theme/tokens";
import { DesignerZone, WIDGET_LABELS, WidgetType, isWebsiteFamilyType, editorWidgetKind } from "./types";
import WebZoneView from "../../../Components/WebZone/WebZoneView";
import { IMedia } from "../../../Api/Interface/api.interface";
import { zoneMediaId, zoneIsIncomplete } from "./mediaUtils";
import { ZoneWidget } from "../../Player/LayoutRenderer";
import { IPlayerZone } from "../../Player/types";

/**
 * Canvas + zone editor. Renders the template at its true aspect ratio
 * with all zones positioned in percentage space. Zones are draggable
 * by their body and resizable via 8 handles. We use native pointer
 * events directly — no react-dnd, no react-rnd — so the build doesn't
 * pick up another dependency just for this one screen.
 *
 * Coordinate model (single source of truth = the canvas element rect):
 *   - All zone state is stored in percentages (0..100) of the canvas.
 *   - The canvas element is always rendered at the template's true
 *     aspect ratio (see the `maxWidth` trick below), so 1px of the
 *     rendered canvas maps linearly to a fixed % — no editor-scale
 *     factor ever enters the math, and it is never applied twice.
 *   - MOVE: on drag start we capture the pointer's offset *inside* the
 *     zone. On every move we read the *live* canvas rect and place the
 *     zone's top-left = pointer − canvasOrigin − grabOffset, converted
 *     to %. Re-reading the rect each move keeps drags accurate even if
 *     the page scrolls / the window resizes / the sidebar toggles
 *     mid-drag.
 *   - RESIZE: pointer delta ÷ live rect size, applied to the start zone.
 *   - Final values are clamped so every zone stays fully on-canvas
 *     (x∈[0,100-w], y∈[0,100-h]); the user can never drag off-canvas.
 * There is exactly one drag flow — no CSS transform is applied, so the
 * visual position and the stored %s can never be double-counted.
 */

type DragMode =
  | { kind: "move" }
  | { kind: "resize"; handle: ResizeHandle };

type ResizeHandle = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

interface DragState {
  uid: string;
  mode: DragMode;
  startZone: DesignerZone;
  // MOVE: pointer offset inside the zone, in rendered canvas px.
  grabX: number;
  grabY: number;
  // RESIZE: pointer position at drag start, in client px.
  startClientX: number;
  startClientY: number;
}

interface CanvasProps {
  width: number;
  height: number;
  background: string;
  zones: DesignerZone[];
  selectedUid: string | null;
  interactUid?: string | null;
  onSelect: (uid: string | null) => void;
  onZoneChange: (uid: string, patch: Partial<DesignerZone>) => void;
  /** Resolved media records keyed by id, for image/video zone previews. */
  mediaById?: Record<number, IMedia>;
}

const HANDLE_SIZE = 10;
/** Minimum zone size as a % of the canvas (matches the inspector clamp). */
const MIN_PCT = 5;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

const Canvas: React.FC<CanvasProps> = ({
  width, height, background, zones, selectedUid, interactUid = null, onSelect, onZoneChange,
  mediaById = {},
}) => {
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragState | null>(null);

  // The parent recreates `onSelect`/`onZoneChange` on every render (they are
  // plain closures, not memoised). If the drag handlers below closed over
  // those props directly they would change identity every render, and the
  // window listeners attached in startDrag would be torn down by the cleanup
  // effect on the FIRST re-render (which onZoneChange itself triggers) — so a
  // resize would advance one frame and then freeze. Reading the live props
  // through refs keeps the handlers STABLE for the whole component lifetime,
  // so the listeners added on pointerdown survive until pointerup/unmount.
  const onZoneChangeRef = useRef(onZoneChange);
  const onSelectRef = useRef(onSelect);
  onZoneChangeRef.current = onZoneChange;
  onSelectRef.current = onSelect;

  const handlePointerMove = useCallback((e: PointerEvent) => {
    const d = dragRef.current;
    const canvas = canvasRef.current;
    if (!d || !canvas) return;

    // Re-read the canvas rect every move: this is the ONLY source of
    // truth for the pixel<->% mapping and it stays correct even if the
    // page scrolls or the window resizes mid-drag.
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    let { x, y, w, h } = d.startZone;

    if (d.mode.kind === "move") {
      // Zone top-left = pointer, minus the canvas origin, minus the
      // grab offset captured at drag start. Keeps the pointer at the
      // same relative point inside the zone the whole time.
      const leftPx = (e.clientX - rect.left) - d.grabX;
      const topPx = (e.clientY - rect.top) - d.grabY;
      x = clamp((leftPx / rect.width) * 100, 0, 100 - w);
      y = clamp((topPx / rect.height) * 100, 0, 100 - h);
    } else {
      const dxPct = ((e.clientX - d.startClientX) / rect.width) * 100;
      const dyPct = ((e.clientY - d.startClientY) / rect.height) * 100;
      const h0 = d.mode.handle;
      // Horizontal edge. MIN_PCT keeps a resized zone from collapsing below
      // the minimum size (matches the inspector's manual-entry clamp).
      if (h0.includes("e")) {
        w = clamp(d.startZone.w + dxPct, MIN_PCT, 100 - d.startZone.x);
      } else if (h0.includes("w")) {
        const newX = clamp(d.startZone.x + dxPct, 0, d.startZone.x + d.startZone.w - MIN_PCT);
        w = d.startZone.x + d.startZone.w - newX;
        x = newX;
      }
      // Vertical edge.
      if (h0.includes("s")) {
        h = clamp(d.startZone.h + dyPct, MIN_PCT, 100 - d.startZone.y);
      } else if (h0.includes("n")) {
        const newY = clamp(d.startZone.y + dyPct, 0, d.startZone.y + d.startZone.h - MIN_PCT);
        h = d.startZone.y + d.startZone.h - newY;
        y = newY;
      }
    }

    // Keep 2 decimals: smooth sub-percent placement without jitter, and
    // matches what the inspector fields display.
    onZoneChangeRef.current(d.uid, {
      x: Math.round(x * 100) / 100,
      y: Math.round(y * 100) / 100,
      w: Math.round(w * 100) / 100,
      h: Math.round(h * 100) / 100,
    });
  }, []);

  const handlePointerUp = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
    window.removeEventListener("pointercancel", handlePointerUp);
  }, [handlePointerMove]);

  const startDrag = useCallback((
    e: React.PointerEvent,
    zone: DesignerZone,
    mode: DragMode,
  ) => {
    // A resize handle press must NOT also start a move, and a body
    // press must NOT bubble to the canvas' deselect handler. stopPropagation
    // guarantees exactly one drag flow starts.
    e.preventDefault();
    e.stopPropagation();
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    // Pointer offset inside the zone, in rendered canvas px, derived from
    // the zone's stored %s against the live rect. Preserved for the whole
    // drag so the zone never jumps its top-left under the pointer.
    const grabX = (e.clientX - rect.left) - (zone.x / 100) * rect.width;
    const grabY = (e.clientY - rect.top) - (zone.y / 100) * rect.height;
    dragRef.current = {
      uid: zone._uid,
      mode,
      startZone: { ...zone },
      grabX,
      grabY,
      startClientX: e.clientX,
      startClientY: e.clientY,
    };
    onSelectRef.current(zone._uid);
    // Global (window-level) listeners so the drag keeps tracking even when the
    // pointer leaves the small handle, crosses the iframe, or exits the canvas.
    // These handlers are stable (see the refs above), so the cleanup effect
    // below never tears them down mid-drag.
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);
  }, [handlePointerMove, handlePointerUp]);

  // Always cleanup on unmount. handlePointerMove/Up are stable, so this effect
  // runs its cleanup only when the component unmounts — never on a re-render.
  useEffect(() => () => {
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
    window.removeEventListener("pointercancel", handlePointerUp);
  }, [handlePointerMove, handlePointerUp]);

  // Render the canvas at the template's TRUE aspect ratio, capped to the
  // viewport height. Using `width: 100%` + `aspectRatio` alone keeps the
  // ratio exact; a bare `maxHeight` would clip the height and silently
  // distort the ratio (breaking the pixel<->% mapping the drag relies on).
  // Instead we cap the *width* to whatever keeps the derived height within
  // the viewport — width = maxHeight * (w/h) — so the element is never
  // distorted in any orientation, and getBoundingClientRect() always
  // reports true, undistorted dimensions.
  const aspect = `${width} / ${height}`;
  const maxCanvasWidth = `calc((100vh - 240px) * ${width / height})`;

  return (
    <Box
      ref={canvasRef}
      onPointerDown={() => onSelect(null)}
      sx={{
        width: "100%",
        maxWidth: maxCanvasWidth,
        mx: "auto",
        aspectRatio: aspect,
        position: "relative",
        background,
        backgroundSize: "cover",
        border: `1px solid ${BORDER}`,
        boxShadow: "0 4px 24px rgba(11,30,45,0.18)",
        overflow: "hidden",
        // The "canvas" is the design surface — disable text-select so
        // dragging doesn't accidentally start a selection.
        userSelect: "none",
        touchAction: "none",
      }}
    >
      {zones.map((z) => {
        const selected = z._uid === selectedUid;
        const isWeb = isWebsiteFamilyType(z.widget_type);
        const interacting = isWeb && z._uid === interactUid;
        const incomplete = zoneIsIncomplete(z);
        return (
          <Box
            key={z._uid}
            // For non-web zones the body is the drag handle. Web zones use a
            // dedicated overlay (below) so the iframe can never swallow the
            // drag; while interacting the iframe owns the pointer entirely.
            onPointerDown={!isWeb && !interacting ? (e) => startDrag(e, z, { kind: "move" }) : undefined}
            sx={{
              position: "absolute",
              left: `${z.x}%`,
              top: `${z.y}%`,
              width: `${z.w}%`,
              height: `${z.h}%`,
              zIndex: z.z_order + 1,
              border: selected
                ? `2px solid ${GOLD}`
                : incomplete
                  ? `2px dashed ${ERROR}`
                  : "1px dashed rgba(255,255,255,0.55)",
              outline: selected ? `4px solid rgba(212,175,55,0.18)` : "none",
              background: isWeb ? "transparent" : (selected ? "rgba(212,175,55,0.08)" : "rgba(255,255,255,0.04)"),
              cursor: interacting ? "default" : "move",
              color: "#fff",
              // Overflow is VISIBLE on the frame so the resize handles (which
              // sit at negative offsets on the border) are never clipped. The
              // inner content box below clips the web/iframe content instead.
              overflow: "visible",
            }}
          >
            {/* Clipped content layer. */}
            <Box sx={{
              position: "absolute", inset: 0, overflow: "hidden",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {isWeb
                ? <WebZonePreview zone={z} interactive={interacting} />
                : <ZonePreview zone={z} media={mediaById[zoneMediaId(z) ?? -1]} />}
            </Box>

            {/* Editing overlay for website zones: sits ABOVE the iframe and
                captures the drag so the embedded page never blocks moving the
                zone. Removed in interaction mode so the site becomes usable. */}
            {isWeb && !interacting && (
              <Box
                onPointerDown={(e) => startDrag(e, z, { kind: "move" })}
                sx={{
                  position: "absolute", inset: 0, zIndex: 2,
                  cursor: "move", background: "transparent",
                }}
              />
            )}

            {selected && !interacting && (
              <>
                {(["n", "s", "e", "w", "ne", "nw", "se", "sw"] as ResizeHandle[]).map((h) => (
                  <ResizeHandleEl
                    key={h} handle={h}
                    onPointerDown={(e) => startDrag(e, z, { kind: "resize", handle: h })}
                  />
                ))}
              </>
            )}
          </Box>
        );
      })}

      {zones.length === 0 && (
        <Box sx={{
          position: "absolute", inset: 0,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          color: "rgba(255,255,255,0.5)", pointerEvents: "none",
        }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, letterSpacing: "0.08em" }}>
            EMPTY CANVAS
          </Typography>
          <Typography sx={{ fontSize: 12, mt: 0.5 }}>
            Pick a widget from the palette to add a zone.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/**
 * Map an IMedia record into the resolved `media` block the shared ZoneWidget
 * expects (identical shape to the device layout + /templates/{id} preview).
 */
const toZoneMedia = (m?: IMedia) =>
  m ? {
    id: m.id,
    type: m.media_type,
    url: m.url,
    checksum: m.checksum ?? null,
    file_size: m.file_size ?? 0,
    duration: m.duration ?? null,
  } : null;

/**
 * Editor zone body — renders the REAL widget content via the SHARED ZoneWidget
 * (same component as the Template Preview and the device player), so text/clock/
 * qr/weather/image/video look exactly like the TV. The content layer is
 * pointer-inert so the zone body still receives drag/resize pointer events. A
 * small widget-type badge is an EDITOR-ONLY overlay in the corner — it never
 * replaces the content.
 */
const ZonePreview: React.FC<{ zone: DesignerZone; media?: IMedia }> = ({ zone, media }) => {
  const t = zone.widget_type as WidgetType;
  const label = WIDGET_LABELS[t] ?? t;

  // For image/video, inject the resolved media so the shared widget can render
  // it; other widgets render straight from their config.
  const cfg = { ...(zone.widget_config as Record<string, unknown>) };
  if (t === "image" || t === "video") {
    const zm = toZoneMedia(media);
    if (zm) cfg.media = zm;
  }
  const playerZone: IPlayerZone = {
    id: zone.id ?? 0,
    name: zone.name ?? null,
    x: zone.x, y: zone.y, w: zone.w, h: zone.h,
    z_order: zone.z_order,
    widget_type: zone.widget_type,
    widget_config: cfg,
  };

  return (
    <Box sx={{ position: "absolute", inset: 0 }}>
      {/* Real content — identical to the player. Pointer-inert so the zone
          stays draggable/resizable in the editor. */}
      <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
        <ZoneWidget zone={playerZone} />
      </Box>
      {/* Editor-only badge (corner chrome) — removed in Preview/player. */}
      <Typography sx={{
        position: "absolute", top: 4, left: 4, pointerEvents: "none",
        fontSize: 9.5, fontWeight: 800, color: NAVY, background: "rgba(255,255,255,0.9)",
        px: 0.6, py: 0.15, borderRadius: 0.5, letterSpacing: "0.04em",
        textTransform: "uppercase", opacity: 0.85,
      }}>
        {zone.name ? `${label} · ${zone.name}` : label}
      </Typography>
    </Box>
  );
};

/**
 * Live embedded-content preview inside the editor canvas. Pointer events
 * are disabled unless the zone is in interaction mode, so the operator
 * can still select / drag / resize a website zone that would otherwise
 * swallow the pointer. A small badge names the zone since the live
 * content can look like any page.
 */
const WebZonePreview: React.FC<{ zone: DesignerZone; interactive: boolean }> = ({ zone, interactive }) => (
  <Box sx={{ position: "absolute", inset: 0 }}>
    <WebZoneView config={zone.widget_config as Record<string, any>} interactive={interactive} />
    <Box sx={{
      position: "absolute", top: 4, left: 4, pointerEvents: "none",
      display: "flex", gap: 0.5, alignItems: "center",
    }}>
      <Typography sx={{ fontSize: 10, fontWeight: 800, color: NAVY,
        background: "#fff", px: 0.75, py: 0.25, borderRadius: 0.5,
        letterSpacing: "0.04em", textTransform: "uppercase", opacity: 0.9 }}>
        {zone.name || WIDGET_LABELS[editorWidgetKind(zone)]}
      </Typography>
      {interactive && (
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#fff",
          background: GOLD, px: 0.75, py: 0.25, borderRadius: 0.5 }}>
          LIVE
        </Typography>
      )}
    </Box>
  </Box>
);

const ResizeHandleEl: React.FC<{
  handle: ResizeHandle;
  onPointerDown: (e: React.PointerEvent) => void;
}> = ({ handle, onPointerDown }) => {
  const map: Record<ResizeHandle, React.CSSProperties> = {
    n:  { top: -HANDLE_SIZE/2, left: `calc(50% - ${HANDLE_SIZE/2}px)`, cursor: "ns-resize" },
    s:  { bottom: -HANDLE_SIZE/2, left: `calc(50% - ${HANDLE_SIZE/2}px)`, cursor: "ns-resize" },
    e:  { right: -HANDLE_SIZE/2, top: `calc(50% - ${HANDLE_SIZE/2}px)`, cursor: "ew-resize" },
    w:  { left: -HANDLE_SIZE/2, top: `calc(50% - ${HANDLE_SIZE/2}px)`, cursor: "ew-resize" },
    ne: { top: -HANDLE_SIZE/2, right: -HANDLE_SIZE/2, cursor: "nesw-resize" },
    nw: { top: -HANDLE_SIZE/2, left: -HANDLE_SIZE/2, cursor: "nwse-resize" },
    se: { bottom: -HANDLE_SIZE/2, right: -HANDLE_SIZE/2, cursor: "nwse-resize" },
    sw: { bottom: -HANDLE_SIZE/2, left: -HANDLE_SIZE/2, cursor: "nesw-resize" },
  };
  return (
    <Box
      onPointerDown={onPointerDown}
      sx={{
        position: "absolute",
        // Above the drag overlay (zIndex 2) so a corner/edge press always
        // resizes rather than starting a move.
        zIndex: 3,
        width: HANDLE_SIZE, height: HANDLE_SIZE,
        background: GOLD, borderRadius: "2px",
        border: "1px solid #fff",
        ...map[handle],
      }}
    />
  );
};

export default Canvas;
